import React, { useMemo } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { getTranslation } from 'helpers/getTranslation';
import { idxMap } from 'helpers/styleMapIdx';

import { Typography } from 'components/shared/Typography';

import { Tooltip } from 'components/Tooltip';

import {
  buildScreenFlowSegments,
  formatClock,
  formatDuration,
  getScreenFlowSegmentTitle,
  type SessionScreen,
} from '../../TimelineSection.helpers';

import styles from './ScreenFlowBar.module.scss';

const TONE_VARIANTS = {
  error: styles.segmentError,
  slow: styles.segmentSlow,
  goal: styles.segmentGoal,
  normal: styles.segmentNormal,
};

/** Screens of the session as a horizontal bar: width ∝ time on screen, click scrolls the log. */
const ScreenFlowBar = ({
  durationMs,
  screens,
  onScreenJump,
}: {
  durationMs: number;
  screens: SessionScreen[];
  onScreenJump: (screenIndex: number) => void;
}) => {
  const { i18n } = useTranslation();
  const segments = useMemo(
    () => buildScreenFlowSegments(screens, durationMs),
    [screens, durationMs, i18n.language],
  );

  if (segments.length === 0) {
    return null;
  }

  return (
    <div className={styles.flow}>
      <div className={styles.header}>
        <Typography
          className={styles.title}
          variant="body3"
          weight="medium"
          mode="compact"
        >
          {getTranslation('student_page.journey.detail.flow_title')}
        </Typography>
        <Typography className={styles.hint} variant="body3" mode="compact">
          {getTranslation('student_page.journey.detail.flow_hint')}
        </Typography>
      </div>
      <div className={styles.scroll}>
        <div className={styles.bar}>
          {segments.map((segment) => {
            const segmentTitle = getScreenFlowSegmentTitle(segment);

            return (
              <div
                key={segment.index}
                className={styles.segmentSlot}
                style={{
                  flexGrow: segment.grow,
                  minWidth: segment.minWidthPx,
                }}
              >
                <Tooltip
                  text={segmentTitle}
                  triggerClassName={styles.segmentTrigger}
                  triggerElement={
                    <button
                      type="button"
                      aria-label={segmentTitle}
                      className={cn(
                        styles.segment,
                        idxMap(
                          TONE_VARIANTS as Record<string, string>,
                          segment.tone,
                        ),
                        { [styles.segmentExit]: segment.isExit },
                      )}
                      onClick={() => onScreenJump(segment.index)}
                    >
                      <Typography
                        className={styles.segmentLabel}
                        component="span"
                        variant="body3"
                        weight="medium"
                        mode="compact"
                      >
                        {/* Too narrow for a word: only the duration, the name is in the tooltip. */}
                        {segment.label ?? ' '}
                      </Typography>
                      <Typography
                        className={styles.segmentDuration}
                        component="span"
                        variant="caption"
                        mode="compact"
                      >
                        {/* The exit is marked by the dark right edge; "выход" lives in the tooltip so the duration never truncates. */}
                        {formatDuration(segment.durationMs)}
                      </Typography>
                    </button>
                  }
                />
              </div>
            );
          })}
        </div>
        <div className={styles.axis}>
          <Typography
            className={styles.axisLabel}
            component="span"
            variant="caption"
            mode="compact"
          >
            0:00
          </Typography>
          <Typography
            className={styles.axisLabel}
            component="span"
            variant="caption"
            mode="compact"
          >
            {formatClock(durationMs / 2)}
          </Typography>
          <Typography
            className={styles.axisLabel}
            component="span"
            variant="caption"
            mode="compact"
          >
            {formatClock(durationMs)}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ScreenFlowBar) as any;
